import React from 'react';
import { Link } from 'react-router-dom';
import { UserCheck, FileCheck, HeartHandshake, ArrowRight, MapPin, Building2, Mail, Phone, Award } from 'lucide-react';
import { Button } from '../components/ui/button';
import SEO from '../components/SEO';
import { useLang } from '../context/LanguageContext';
import { COMPANY, IMAGES, STATS } from '../mock';

const About = () => {
  const { t, lang } = useLang();
  const icons = [UserCheck, FileCheck, HeartHandshake];

  return (
    <div>
      <SEO page="about" />
      <section className="relative bg-gradient-to-b from-slate-50 to-white pt-16 md:pt-24 pb-14 md:pb-20">
        <div className="max-w-7xl mx-auto px-5 md:px-8 grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
          <div className="lg:col-span-6">
            <div className="inline-flex items-center gap-2 text-[11.5px] font-semibold uppercase tracking-[0.18em] text-blue-800">
              <span className="w-6 h-px bg-red-500" />{t.about.eyebrow}
            </div>
            <h1 className="mt-5 text-[40px] md:text-[52px] leading-[1.05] font-semibold tracking-tight text-slate-900">{t.about.title}</h1>
            <p className="mt-6 text-[16.5px] text-slate-600 leading-relaxed">{t.about.p1}</p>
            <p className="mt-4 text-[16.5px] text-slate-600 leading-relaxed">{t.about.p2}</p>

            <div className="mt-8 flex flex-wrap gap-6 text-[13.5px] text-slate-600">
              <div className="flex items-center gap-2"><Building2 size={16} className="text-slate-400" /> <span>{COMPANY.name}</span></div>
              <div className="flex items-center gap-2"><MapPin size={16} className="text-slate-400" /> <span>{COMPANY.address}</span></div>
            </div>
          </div>

          <div className="lg:col-span-6">
            <div className="rounded-3xl overflow-hidden ring-1 ring-slate-900/5 shadow-[0_40px_80px_-30px_rgba(15,23,42,0.35)]">
              <img src={IMAGES.aboutTruck} alt="VenaTerra — Blood of Logistics truck in motion" className="w-full h-[480px] object-cover" loading="lazy" />
            </div>
          </div>
        </div>
      </section>

      {/* Pillars */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 text-[11.5px] font-semibold uppercase tracking-[0.18em] text-blue-800">
              <span className="w-6 h-px bg-red-500" />
              {lang === 'cs' ? 'Naše principy' : 'What we stand for'}
            </div>
            <h2 className="mt-4 text-[30px] md:text-[40px] leading-[1.08] font-semibold tracking-tight text-slate-900">
              {lang === 'cs' ? 'Tři pilíře, na kterých stavíme spolupráci.' : 'Three pillars we build every partnership on.'}
            </h2>
          </div>

          <div className="mt-12 grid md:grid-cols-3 gap-5">
            {t.about.pillars.map((p, i) => {
              const Icon = icons[i];
              return (
                <div key={i} className="relative bg-slate-50 rounded-2xl p-7 border border-slate-200">
                  <div className="w-12 h-12 rounded-xl bg-white ring-1 ring-slate-200 flex items-center justify-center text-blue-800">
                    <Icon size={22} />
                  </div>
                  <h3 className="mt-6 text-[19px] font-semibold text-slate-900 tracking-tight">{p.title}</h3>
                  <p className="mt-2 text-[14.5px] text-slate-600 leading-relaxed">{p.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Certifications & References — VW Group */}
      <section className="py-16 md:py-20 bg-white border-y border-slate-200/70">
        <div className="max-w-7xl mx-auto px-5 md:px-8 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-5">
            <div className="inline-flex items-center gap-2 text-[11.5px] font-semibold uppercase tracking-[0.18em] text-blue-700">
              <span className="w-6 h-px bg-red-500" />
              {lang === 'cs' ? 'Certifikace a reference' : 'Certifications & references'}
            </div>
            <h2 className="mt-4 text-[28px] md:text-[36px] leading-[1.08] font-semibold tracking-tight text-slate-900">
              {lang === 'cs'
                ? 'Prověřený dodavatel automotive průmyslu.'
                : 'A vetted supplier for the automotive industry.'}
            </h2>
          </div>

          <div className="lg:col-span-7">
            <div className="relative bg-gradient-to-br from-[#0B1D4F] to-[#0F2A6E] text-white rounded-2xl p-7 md:p-9 overflow-hidden">
              {/* subtle artery accent */}
              <div className="absolute top-0 left-6 right-6 h-px bg-gradient-to-r from-transparent via-red-500/70 to-transparent" />
              <div className="flex items-start gap-5">
                <div className="w-14 h-14 rounded-xl bg-white/10 backdrop-blur-sm flex items-center justify-center shrink-0">
                  <Award size={26} strokeWidth={1.8} className="text-white" />
                </div>
                <div className="min-w-0">
                  <div className="text-[11.5px] uppercase tracking-[0.14em] font-semibold text-blue-200">
                    Volkswagen Group Supply
                  </div>
                  <h3 className="mt-2 text-[20px] md:text-[22px] font-semibold tracking-tight">
                    {lang === 'cs'
                      ? 'Registrovaný účastník dodavatelské platformy VW Group Supply'
                      : 'Registered participant in the VW Group Supply platform'}
                  </h3>
                  <p className="mt-3 text-[14.5px] leading-relaxed text-slate-300">
                    {lang === 'cs'
                      ? 'Společnost VenaTerra s.r.o. je registrována v dodavatelském systému Volkswagen Group Supply, který propojuje koncern Volkswagen s jeho obchodními partnery a dodavateli. Splňujeme požadavky na dopravce v automotive průmyslu.'
                      : 'VenaTerra s.r.o. is registered in the Volkswagen Group Supply system, which connects the Volkswagen Group with its business partners and suppliers. We meet the carrier requirements of the automotive industry.'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Leadership */}
      <section className="py-16 md:py-24 bg-slate-50">
        <div className="max-w-6xl mx-auto px-5 md:px-8">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 text-[11.5px] font-semibold uppercase tracking-[0.18em] text-blue-700">
              <span className="w-6 h-px bg-red-500" />
              {t.about.leadership}
            </div>
            <h2 className="mt-4 text-[30px] md:text-[40px] leading-[1.08] font-semibold tracking-tight text-slate-900">
              {t.about.leadershipDesc}
            </h2>
          </div>

          <div className="mt-12 grid md:grid-cols-3 gap-5">
            <div className="md:col-span-2 bg-white rounded-2xl border border-slate-200 p-7 flex flex-col sm:flex-row gap-6 items-start">
              <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-slate-900 to-slate-700 text-white flex items-center justify-center text-[26px] font-semibold shrink-0">
                LM
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[11.5px] uppercase tracking-[0.14em] text-blue-700 font-semibold">
                  {lang === 'cs' ? COMPANY.director.roleCs : COMPANY.director.roleEn}
                </div>
                <div className="mt-1 text-[22px] font-semibold text-slate-900 tracking-tight">
                  {COMPANY.director.name}
                </div>
                <p className="mt-3 text-[14.5px] text-slate-600 leading-relaxed">
                  {lang === 'cs'
                    ? 'Odpovědná za strategický rozvoj společnosti, obchodní vztahy a kvalitu služeb. Kontaktujte ji přímo ohledně dlouhodobé spolupráce, tendrů a rámcových smluv.'
                    : 'Responsible for strategic development, business relationships and service quality. Contact her directly regarding long-term partnerships, tenders and framework contracts.'}
                </p>
                <div className="mt-4 flex flex-wrap gap-4 text-[13.5px]">
                  <a href={`mailto:${COMPANY.director.email}`} className="inline-flex items-center gap-1.5 text-slate-700 hover:text-blue-800 transition-colors">
                    <Mail size={15} /> {COMPANY.director.email}
                  </a>
                  <a href={`tel:${COMPANY.phoneRaw}`} className="inline-flex items-center gap-1.5 text-slate-700 hover:text-blue-800 transition-colors">
                    <Phone size={15} /> {COMPANY.phone}
                  </a>
                </div>
              </div>
            </div>

            <div className="bg-[#0F2461] text-white rounded-2xl p-7">
              <div className="text-[11.5px] uppercase tracking-[0.14em] text-blue-400 font-semibold">
                {lang === 'cs' ? 'Přímý kontakt' : 'Direct line'}
              </div>
              <p className="mt-3 text-[15px] text-slate-300 leading-relaxed">
                {lang === 'cs'
                  ? 'Preferujeme přímou komunikaci. Zavolejte, napište — dostanete odpověď od člověka, který o vaší zásilce rozhoduje.'
                  : 'We prefer direct communication. Call or write — you will get an answer from the person actually making decisions about your shipment.'}
              </p>
              <Link to="/contact" className="mt-5 inline-flex items-center gap-1.5 text-[14px] font-medium text-blue-400 hover:text-red-300 transition-colors">
                {t.cta.getInTouch} <ArrowRight size={14} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Stats + CTA */}
      <section className="py-16 md:py-24 bg-[#0B1D4F] text-white relative overflow-hidden">
        <div className="absolute -top-20 -left-10 w-[500px] h-[500px] rounded-full bg-blue-700/20 blur-3xl pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-5 md:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {STATS.map((s) => (
              <div key={s.key}>
                <div className="text-[36px] md:text-[46px] font-semibold tracking-tight leading-none text-white">
                  {lang === 'cs' ? s.valueCs : s.valueEn}
                </div>
                <div className="mt-2 text-[13px] text-slate-400 font-medium">
                  {lang === 'cs' ? s.labelCs : s.labelEn}
                </div>
              </div>
            ))}
          </div>

          <div className="mt-14 flex flex-col md:flex-row md:items-center justify-between gap-6">
            <h3 className="text-[24px] md:text-[30px] font-semibold tracking-tight max-w-2xl">
              {lang === 'cs' ? 'Poznejte tým, který stojí za každou zásilkou.' : 'Meet the team behind every shipment.'}
            </h3>
            <Link to="/contact">
              <Button className="bg-blue-700 hover:bg-blue-800 text-white rounded-full px-6 h-12 text-[15px] font-medium">
                {t.cta.getInTouch}
                <ArrowRight size={17} className="ml-1.5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
